import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  Table,
  Input,
  FormGroup,
  Label,
  Spinner,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap';
import { get } from 'helpers/api_helper';
import { ACCOUNTS_PASSBOOK_URL, BILLING_URL, PAYMENT_URL, VOUCHER_URL } from 'helpers/url_helper';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_blue.css';
import cookieHelper from 'helpers/getCookieData';
import {
  RiBookOpenLine,
  RiArrowUpLine,
  RiArrowDownLine,
  RiPrinterLine,
  RiDownloadLine,
  RiRefreshLine,
  RiCalendarEventLine,
  RiSearchLine,
  RiFilter3Line,
  RiExchangeDollarLine,
  RiWallet3Line,
  RiHandCoinLine,
  RiBankLine,
  RiShieldCheckLine,
  RiFileExcel2Line,
  RiSortAsc,
  RiSortDesc
} from 'react-icons/ri';

const ManagePassbook = () => {
  const { t } = useTranslation();

  const clinic = useMemo(() => {
    try {
      const raw = cookieHelper.getCookie('_c');
      return raw ? JSON.parse(atob(raw)) : null;
    } catch (e) {
      return null;
    }
  }, []);

  // Filter States
  const [filterPreset, setFilterPreset] = useState('this_fy'); // 'all', 'today', 'yesterday', 'this_week', 'this_month', 'last_month', 'this_fy', 'last_fy', 'custom', 'by_month', 'by_fy'
  const [customRange, setCustomRange] = useState([
    moment().startOf('month').toDate(),
    moment().endOf('month').toDate()
  ]);
  const [selectedMonth, setSelectedMonth] = useState(moment().format('MM'));
  const [selectedYear, setSelectedYear] = useState(moment().format('YYYY'));
  
  // Calculate current Indian Financial Year (Apr - Mar)
  const currentFYString = useMemo(() => {
    const now = moment();
    const startYr = now.month() >= 3 ? now.year() : now.year() - 1;
    return `${startYr}-${startYr + 1}`;
  }, []);

  const [selectedFY, setSelectedFY] = useState(currentFYString);
  const [typeFilter, setTypeFilter] = useState('all'); // 'all', 'credit', 'debit'
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [paymentModeFilter, setPaymentModeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' (newest first) or 'asc' (oldest first)

  // Data States
  const [loading, setLoading] = useState(false);
  const [passbookData, setPassbookData] = useState({
    summary: {
      totalCredit: 0,
      totalDebit: 0,
      netCashflow: 0,
      totalTransactions: 0,
      currentBalance: 0,
      modeTotals: {}
    },
    items: []
  });

  // Fetch unified passbook records
  const fetchPassbook = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('preset', filterPreset);

      if (filterPreset === 'custom' && customRange.length === 2 && customRange[0] && customRange[1]) {
        params.append('startDate', moment(customRange[0]).format('YYYY-MM-DD'));
        params.append('endDate', moment(customRange[1]).format('YYYY-MM-DD'));
      } else if (filterPreset === 'by_month') {
        params.append('month', selectedMonth);
        params.append('year', selectedYear);
      } else if (filterPreset === 'by_fy') {
        params.append('financialYear', selectedFY);
      }

      if (typeFilter !== 'all') params.append('type', typeFilter);
      if (paymentModeFilter !== 'all') params.append('payment_mode', paymentModeFilter);
      if (searchQuery.trim()) params.append('search', searchQuery.trim());

      const res = await get(`${ACCOUNTS_PASSBOOK_URL}?${params.toString()}`);
      const data = res?.body || res?.data || res;

      if (data && data.success && Array.isArray(data.items)) {
        setPassbookData({
          summary: data.summary || {
            totalCredit: 0,
            totalDebit: 0,
            netCashflow: 0,
            totalTransactions: data.items.length,
            currentBalance: 0,
            modeTotals: {}
          },
          items: data.items
        });
      } else {
        // Fallback calculation if route returned empty or unexpected structure
        setPassbookData({
          summary: { totalCredit: 0, totalDebit: 0, netCashflow: 0, totalTransactions: 0, currentBalance: 0, modeTotals: {} },
          items: []
        });
      }
    } catch (e) {
      console.error("Error fetching passbook statement:", e);
    } finally {
      setLoading(false);
    }
  }, [filterPreset, customRange, selectedMonth, selectedYear, selectedFY, typeFilter, paymentModeFilter, searchQuery]);

  useEffect(() => {
    fetchPassbook();
  }, [fetchPassbook]);

  // Client-side category filtering & sorting
  const displayedItems = useMemo(() => {
    let list = [...(passbookData.items || [])];

    if (categoryFilter !== 'all') {
      list = list.filter(item => {
        if (categoryFilter === 'patient' && item.source === 'payment_receipt') return true;
        if (categoryFilter === 'petty_cash_expense' && item.source === 'petty_cash' && item.type === 'Debit') return true;
        if (categoryFilter === 'petty_cash_receipt' && item.source === 'petty_cash' && item.type === 'Credit') return true;
        if (categoryFilter === 'bank_deposit' && item.source === 'bank_deposit') return true;
        return false;
      });
    }

    if (sortOrder === 'asc') {
      list.sort((a, b) => new Date(a.date) - new Date(b.date));
    } else {
      list.sort((a, b) => new Date(b.date) - new Date(a.date));
    }

    return list;
  }, [passbookData.items, categoryFilter, sortOrder]);

  // Export to CSV
  const handleExportCSV = () => {
    if (!displayedItems || displayedItems.length === 0) return;

    const headers = [
      '#',
      'Date & Time',
      'Ref Number',
      'Party / Particulars',
      'Category',
      'Payment Mode',
      'Credit (Income INR)',
      'Debit (Expense INR)',
      'Running Balance (INR)',
      'Status'
    ];

    const rows = displayedItems.map((item, idx) => [
      idx + 1,
      item.date ? moment(item.date).format('YYYY-MM-DD HH:mm') : '',
      `"${item.ref_no || ''}"`,
      `"${(item.party_name || '').replace(/"/g, '""')}"`,
      `"${item.category || ''}"`,
      `"${item.payment_mode || ''}"`,
      item.credit || 0,
      item.debit || 0,
      item.balance || 0,
      item.status || 'Completed'
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Financial_Passbook_Statement_${moment().format('YYYYMMDD_HHmm')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Printable Passbook Window
  const handlePrintPassbook = () => {
    const printWin = window.open('', '_blank', 'width=1100,height=800');
    if (!printWin) return;

    const clinicName = clinic?.clinic_name || '909 Dental OPD & Specialized Oral Care';
    const clinicAddress = clinic?.address || 'Modern Dental Clinic, Suite 101';
    const clinicPhone = clinic?.phone || clinic?.mobile || 'Clinic Contact';

    const periodLabel = filterPreset === 'today' ? 'Today' :
      filterPreset === 'this_month' ? `This Month (${moment().format('MMMM YYYY')})` :
      filterPreset === 'last_month' ? `Last Month (${moment().subtract(1, 'months').format('MMMM YYYY')})` :
      filterPreset === 'this_fy' ? `Financial Year ${currentFYString}` :
      filterPreset === 'by_fy' ? `Financial Year ${selectedFY}` :
      filterPreset === 'by_month' ? `Month: ${moment(selectedMonth, 'MM').format('MMMM')} ${selectedYear}` :
      filterPreset === 'custom' ? `${moment(customRange[0]).format('DD MMM YYYY')} to ${moment(customRange[1]).format('DD MMM YYYY')}` :
      'All Time';

    const tableRowsHtml = displayedItems.map((item, idx) => `
      <tr style="border-bottom: 1px solid #e9ecef; font-size: 11px;">
        <td style="padding: 8px 6px; text-align: center;">${idx + 1}</td>
        <td style="padding: 8px 6px; white-space: nowrap;">${moment(item.date).format('DD MMM YYYY, hh:mm A')}</td>
        <td style="padding: 8px 6px; font-family: monospace; font-weight: bold;">${item.ref_no || '—'}</td>
        <td style="padding: 8px 6px;">
          <strong>${item.party_name || 'Particulars'}</strong>
          ${item.party_detail ? `<div style="color: #6c757d; font-size: 10px;">${item.party_detail}</div>` : ''}
          <div style="color: #495057; font-size: 10px;">${item.description || ''}</div>
        </td>
        <td style="padding: 8px 6px;">${item.category || 'General'}</td>
        <td style="padding: 8px 6px; text-align: center;">${item.payment_mode || 'Cash'}</td>
        <td style="padding: 8px 6px; text-align: right; color: #198754; font-weight: bold;">
          ${item.credit > 0 ? `+₹${parseFloat(item.credit).toLocaleString('en-IN')}` : '—'}
        </td>
        <td style="padding: 8px 6px; text-align: right; color: #dc3545; font-weight: bold;">
          ${item.debit > 0 ? `-₹${parseFloat(item.debit).toLocaleString('en-IN')}` : '—'}
        </td>
        <td style="padding: 8px 6px; text-align: right; font-weight: bold; color: ${item.balance >= 0 ? '#0d6efd' : '#dc3545'};">
          ₹${parseFloat(item.balance || 0).toLocaleString('en-IN')}
        </td>
      </tr>
    `).join('');

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Financial Passbook Statement — ${clinicName}</title>
        <meta charset="utf-8" />
        <style>
          @page { size: A4 portrait; margin: 15mm 10mm; }
          body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif; color: #212529; margin: 0; padding: 20px; font-size: 12px; }
          .header-box { border-bottom: 2px solid #0d6efd; padding-bottom: 12px; margin-bottom: 15px; display: flex; justify-content: space-between; align-items: flex-start; }
          .title { font-size: 20px; font-weight: bold; color: #0d6efd; margin: 0 0 4px 0; }
          .subtitle { font-size: 13px; font-weight: 600; color: #333; margin-bottom: 4px; }
          .clinic-meta { font-size: 11px; color: #6c757d; }
          .kpi-container { display: flex; gap: 12px; margin-bottom: 15px; }
          .kpi-card { flex: 1; border: 1px solid #dee2e6; border-radius: 6px; padding: 10px; background: #f8f9fa; }
          .kpi-card.credit { border-left: 4px solid #198754; }
          .kpi-card.debit { border-left: 4px solid #dc3545; }
          .kpi-card.balance { border-left: 4px solid #0d6efd; }
          .kpi-title { font-size: 10px; text-transform: uppercase; color: #6c757d; font-weight: bold; margin-bottom: 2px; }
          .kpi-val { font-size: 16px; font-weight: bold; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th { background: #f1f3f5; border-bottom: 2px solid #ced4da; padding: 8px 6px; text-align: left; font-size: 10px; text-transform: uppercase; color: #495057; }
          .footer { margin-top: 25px; padding-top: 10px; border-top: 1px dashed #ced4da; display: flex; justify-content: space-between; font-size: 10px; color: #6c757d; }
          @media print { .no-print { display: none; } body { padding: 0; } }
        </style>
      </head>
      <body>
        <div class="no-print" style="margin-bottom: 15px; text-align: right;">
          <button onclick="window.print()" style="padding: 8px 18px; background: #0d6efd; color: #fff; border: none; border-radius: 4px; cursor: pointer; font-weight: bold;">
            🖨️ Print / Save as PDF
          </button>
        </div>

        <div class="header-box">
          <div>
            <div class="title">PRACTICE FINANCIAL PASSBOOK</div>
            <div class="subtitle">Unified Clinic Cashflow & Treatment Revenue Ledger</div>
            <div class="clinic-meta">${clinicName} • ${clinicAddress} • 📞 ${clinicPhone}</div>
          </div>
          <div style="text-align: right;">
            <div style="font-weight: bold; font-size: 12px;">Statement Period:</div>
            <div style="color: #0d6efd; font-weight: bold; font-size: 13px;">${periodLabel}</div>
            <div style="font-size: 10px; color: #6c757d; margin-top: 3px;">Generated: ${moment().format('DD MMM YYYY, hh:mm A')}</div>
          </div>
        </div>

        <div class="kpi-container">
          <div class="kpi-card credit">
            <div class="kpi-title">Total Inflows (Credits)</div>
            <div class="kpi-val" style="color: #198754;">₹${(passbookData.summary.totalCredit || 0).toLocaleString('en-IN')}</div>
          </div>
          <div class="kpi-card debit">
            <div class="kpi-title">Total Outflows (Debits)</div>
            <div class="kpi-val" style="color: #dc3545;">₹${(passbookData.summary.totalDebit || 0).toLocaleString('en-IN')}</div>
          </div>
          <div class="kpi-card balance">
            <div class="kpi-title">Net Cashflow for Period</div>
            <div class="kpi-val" style="color: #0d6efd;">₹${(passbookData.summary.netCashflow || 0).toLocaleString('en-IN')}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-title">Total Transactions</div>
            <div class="kpi-val" style="color: #333;">${displayedItems.length} Entries</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 30px; text-align: center;">#</th>
              <th style="width: 110px;">Date & Time</th>
              <th style="width: 95px;">Ref #</th>
              <th>Particulars & Description</th>
              <th style="width: 120px;">Category</th>
              <th style="width: 70px; text-align: center;">Mode</th>
              <th style="width: 85px; text-align: right;">Credit (+)</th>
              <th style="width: 85px; text-align: right;">Debit (-)</th>
              <th style="width: 95px; text-align: right;">Balance</th>
            </tr>
          </thead>
          <tbody>
            ${tableRowsHtml || '<tr><td colspan="9" style="text-align: center; padding: 20px; color: #888;">No financial records found for this period.</td></tr>'}
          </tbody>
        </table>

        <div class="footer">
          <div>Verified Computer-Generated Financial Statement • 909 Dental OPD Software</div>
          <div>Authorized Clinic Accountant / Manager Signatory</div>
        </div>
      </body>
      </html>
    `;

    printWin.document.open();
    printWin.document.write(htmlContent);
    printWin.document.close();
  };

  // Month list for dropdown
  const monthOptions = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  // Financial Year list
  const fyOptions = [
    '2027-2028',
    '2026-2027',
    '2025-2026',
    '2024-2025',
    '2023-2024',
    '2022-2023'
  ];

  // Year list
  const yearOptions = [2027, 2026, 2025, 2024, 2023, 2022];

  return (
    <div className="practice-passbook-wrapper">
      {/* Header & Quick Action Buttons */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h5 className="font-weight-bold text-dark font-size-16 mb-0 d-flex align-items-center gap-2">
            <RiBookOpenLine className="text-primary font-size-20" />
            {t("Practice Financial Passbook & Unified Ledger")}
          </h5>
          <small className="text-muted font-size-12">
            {t("Real-time chronological audit trail of treatment receipts, clinic expenses, petty cash, and bank transfers")}
          </small>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <Button
            type="button"
            color="light"
            size="sm"
            className="border d-inline-flex align-items-center gap-1.5 shadow-2xs font-size-12"
            onClick={fetchPassbook}
            disabled={loading}
            title={t("Refresh Passbook")}
          >
            <RiRefreshLine className={loading ? "bx-spin font-size-14" : "font-size-14"} />
            {t("Refresh")}
          </Button>

          <Button
            type="button"
            color="light"
            size="sm"
            className="border text-success d-inline-flex align-items-center gap-1.5 shadow-2xs font-size-12 font-weight-semibold"
            onClick={handleExportCSV}
            disabled={displayedItems.length === 0}
            title={t("Export to CSV Excel")}
          >
            <RiFileExcel2Line className="font-size-14" />
            {t("Export CSV")}
          </Button>

          <Button
            type="button"
            color="primary"
            size="sm"
            className="d-inline-flex align-items-center gap-1.5 shadow-2xs font-size-12 font-weight-semibold"
            onClick={handlePrintPassbook}
            disabled={displayedItems.length === 0}
          >
            <RiPrinterLine className="font-size-14" />
            {t("Print Passbook")}
          </Button>
        </div>
      </div>

      {/* KPI Financial Overview Cards */}
      <Row className="g-3 mb-3">
        <Col sm={6} lg={3}>
          <div className="card border-0 shadow-2xs rounded-3 overflow-hidden h-100" style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', borderLeft: '4px solid #16a34a' }}>
            <div className="card-body p-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <span className="text-success font-size-11 text-uppercase font-weight-bold letter-spacing-1">
                    {t("Total Inflows (Credit)")}
                  </span>
                  <h4 className="font-weight-bold text-success mb-0 mt-1 font-size-20">
                    +₹{(passbookData.summary.totalCredit || 0).toLocaleString('en-IN')}
                  </h4>
                  <div className="font-size-11 text-muted mt-1">
                    Treatment collections & receipts
                  </div>
                </div>
                <div className="rounded-circle bg-success text-white p-2.5 d-flex align-items-center justify-content-center shadow-sm" style={{ width: '42px', height: '42px' }}>
                  <RiArrowDownLine className="font-size-20" />
                </div>
              </div>
            </div>
          </div>
        </Col>

        <Col sm={6} lg={3}>
          <div className="card border-0 shadow-2xs rounded-3 overflow-hidden h-100" style={{ background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)', borderLeft: '4px solid #dc2626' }}>
            <div className="card-body p-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <span className="text-danger font-size-11 text-uppercase font-weight-bold letter-spacing-1">
                    {t("Total Outflows (Debit)")}
                  </span>
                  <h4 className="font-weight-bold text-danger mb-0 mt-1 font-size-20">
                    -₹{(passbookData.summary.totalDebit || 0).toLocaleString('en-IN')}
                  </h4>
                  <div className="font-size-11 text-muted mt-1">
                    Petty cash & clinic expenses
                  </div>
                </div>
                <div className="rounded-circle bg-danger text-white p-2.5 d-flex align-items-center justify-content-center shadow-sm" style={{ width: '42px', height: '42px' }}>
                  <RiArrowUpLine className="font-size-20" />
                </div>
              </div>
            </div>
          </div>
        </Col>

        <Col sm={6} lg={3}>
          <div className="card border-0 shadow-2xs rounded-3 overflow-hidden h-100" style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', borderLeft: '4px solid #2563eb' }}>
            <div className="card-body p-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <span className="text-primary font-size-11 text-uppercase font-weight-bold letter-spacing-1">
                    {t("Net Period Cashflow")}
                  </span>
                  <h4 className={`font-weight-bold mb-0 mt-1 font-size-20 ${(passbookData.summary.netCashflow || 0) >= 0 ? 'text-primary' : 'text-danger'}`}>
                    ₹{(passbookData.summary.netCashflow || 0).toLocaleString('en-IN')}
                  </h4>
                  <div className="font-size-11 text-muted mt-1">
                    Net cash surplus / deficit
                  </div>
                </div>
                <div className="rounded-circle bg-primary text-white p-2.5 d-flex align-items-center justify-content-center shadow-sm" style={{ width: '42px', height: '42px' }}>
                  <RiExchangeDollarLine className="font-size-20" />
                </div>
              </div>
            </div>
          </div>
        </Col>

        <Col sm={6} lg={3}>
          <div className="card border-0 shadow-2xs rounded-3 overflow-hidden h-100" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', borderLeft: '4px solid #64748b' }}>
            <div className="card-body p-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <span className="text-secondary font-size-11 text-uppercase font-weight-bold letter-spacing-1">
                    {t("Entries in Period")}
                  </span>
                  <h4 className="font-weight-bold text-dark mb-0 mt-1 font-size-20">
                    {displayedItems.length} {t("Records")}
                  </h4>
                  <div className="font-size-11 text-muted mt-1">
                    Closing Balance: <strong>₹{(passbookData.summary.currentBalance || 0).toLocaleString('en-IN')}</strong>
                  </div>
                </div>
                <div className="rounded-circle bg-secondary text-white p-2.5 d-flex align-items-center justify-content-center shadow-sm" style={{ width: '42px', height: '42px' }}>
                  <RiWallet3Line className="font-size-20" />
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Advanced Filter Toolbar */}
      <Card className="border shadow-2xs mb-3 bg-light-subtle rounded-3">
        <CardBody className="p-3">
          {/* Preset Buttons Bar */}
          <div className="d-flex flex-wrap align-items-center gap-1.5 mb-3 border-bottom pb-2.5">
            <span className="text-muted font-size-12 font-weight-semibold me-1 d-flex align-items-center gap-1">
              <RiCalendarEventLine /> {t("Period:")}
            </span>

            {[
              { key: 'this_fy', label: `This FY (${currentFYString})` },
              { key: 'this_month', label: 'This Month' },
              { key: 'last_month', label: 'Last Month' },
              { key: 'today', label: 'Today' },
              { key: 'this_week', label: 'This Week' },
              { key: 'by_month', label: '🗓️ By Specific Month' },
              { key: 'by_fy', label: '🏛️ By Financial Year' },
              { key: 'custom', label: '📅 Custom Date Range' },
              { key: 'all', label: 'All Time' },
            ].map(p => (
              <Button
                key={p.key}
                type="button"
                size="sm"
                color={filterPreset === p.key ? "primary" : "light"}
                className={`rounded-pill px-3 py-1 font-size-11 font-weight-semibold shadow-2xs border ${filterPreset === p.key ? 'text-white' : 'text-dark'}`}
                onClick={() => setFilterPreset(p.key)}
              >
                {t(p.label)}
              </Button>
            ))}
          </div>

          {/* Contextual Granular Pickers */}
          <Row className="g-2.5 align-items-end">
            {/* Custom Range Flatpickr */}
            {filterPreset === 'custom' && (
              <Col xs={12} sm={6} md={4} lg={3}>
                <label className="form-label font-size-11 font-weight-semibold text-dark mb-1">
                  {t("Select Date Range (From - To)")}
                </label>
                <Flatpickr
                  className="form-control form-control-sm font-size-12 bg-white"
                  value={customRange}
                  onChange={([start, end]) => {
                    if (start && end) {
                      setCustomRange([start, end]);
                    }
                  }}
                  options={{
                    mode: 'range',
                    dateFormat: 'Y-m-d',
                    altInput: true,
                    altFormat: 'd M Y'
                  }}
                  placeholder={t("Pick date range...")}
                />
              </Col>
            )}

            {/* Specific Month & Year Selector */}
            {filterPreset === 'by_month' && (
              <>
                <Col xs={6} sm={3} md={2}>
                  <label className="form-label font-size-11 font-weight-semibold text-dark mb-1">
                    {t("Select Month")}
                  </label>
                  <select
                    className="form-select form-select-sm font-size-12 bg-white"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                  >
                    {monthOptions.map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </Col>
                <Col xs={6} sm={3} md={2}>
                  <label className="form-label font-size-11 font-weight-semibold text-dark mb-1">
                    {t("Select Year")}
                  </label>
                  <select
                    className="form-select form-select-sm font-size-12 bg-white"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                  >
                    {yearOptions.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </Col>
              </>
            )}

            {/* Specific Financial Year Selector */}
            {filterPreset === 'by_fy' && (
              <Col xs={12} sm={4} md={3}>
                <label className="form-label font-size-11 font-weight-semibold text-dark mb-1">
                  {t("Select Financial Year (Apr - Mar)")}
                </label>
                <select
                  className="form-select form-select-sm font-size-12 bg-white font-weight-semibold"
                  value={selectedFY}
                  onChange={(e) => setSelectedFY(e.target.value)}
                >
                  {fyOptions.map(fy => (
                    <option key={fy} value={fy}>FY {fy} (1 Apr {fy.split('-')[0]} - 31 Mar {fy.split('-')[1]})</option>
                  ))}
                </select>
              </Col>
            )}

            {/* Filter by Type (Credit / Debit) */}
            <Col xs={6} sm={3} md={2}>
              <label className="form-label font-size-11 font-weight-semibold text-dark mb-1">
                {t("Flow Type")}
              </label>
              <select
                className="form-select form-select-sm font-size-12 bg-white"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="all">{t("All Flows (In & Out)")}</option>
                <option value="credit">🟢 {t("Credits Only (Inflows)")}</option>
                <option value="debit">🔴 {t("Debits Only (Outflows)")}</option>
              </select>
            </Col>

            {/* Filter by Category */}
            <Col xs={6} sm={3} md={2}>
              <label className="form-label font-size-11 font-weight-semibold text-dark mb-1">
                {t("Category")}
              </label>
              <select
                className="form-select form-select-sm font-size-12 bg-white"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="all">{t("All Categories")}</option>
                <option value="patient">🦷 {t("Patient Collections")}</option>
                <option value="petty_cash_expense">💸 {t("Petty Cash Expenses")}</option>
                <option value="petty_cash_receipt">📥 {t("Petty Cash Receipts")}</option>
                <option value="bank_deposit">🏦 {t("Bank Deposits")}</option>
              </select>
            </Col>

            {/* Filter by Payment Mode */}
            <Col xs={6} sm={3} md={2}>
              <label className="form-label font-size-11 font-weight-semibold text-dark mb-1">
                {t("Payment Mode")}
              </label>
              <select
                className="form-select form-select-sm font-size-12 bg-white"
                value={paymentModeFilter}
                onChange={(e) => setPaymentModeFilter(e.target.value)}
              >
                <option value="all">{t("All Payment Modes")}</option>
                <option value="Cash">💵 {t("Cash")}</option>
                <option value="UPI">📱 {t("UPI / QR Code")}</option>
                <option value="Card">💳 {t("Card (POS)")}</option>
                <option value="Bank">🏦 {t("Bank Transfer / NEFT")}</option>
                <option value="Cheque">📝 {t("Cheque")}</option>
              </select>
            </Col>

            {/* Search Box */}
            <Col xs={12} sm={6} md={3} className="ms-auto">
              <label className="form-label font-size-11 font-weight-semibold text-dark mb-1">
                {t("Search Particulars / Ref #")}
              </label>
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control form-control-sm pe-4 bg-white"
                  placeholder={t("Search by name, ref, remark...")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <RiSearchLine className="position-absolute end-0 top-50 translate-middle-y me-2 text-muted" />
              </div>
            </Col>

            {/* Sort Order Toggle */}
            <Col xs="auto">
              <Button
                type="button"
                color="light"
                size="sm"
                className="border d-inline-flex align-items-center gap-1 font-size-11"
                onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                title={sortOrder === 'desc' ? t("Showing Newest First") : t("Showing Oldest First")}
              >
                {sortOrder === 'desc' ? (
                  <>
                    <RiSortDesc className="font-size-14 text-primary" /> {t("Newest First")}
                  </>
                ) : (
                  <>
                    <RiSortAsc className="font-size-14 text-primary" /> {t("Oldest First")}
                  </>
                )}
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Main Passbook Data Table */}
      <div className="card border shadow-2xs rounded-3 overflow-hidden">
        <div className="table-responsive">
          <Table hover className="align-middle mb-0 font-size-12 passbook-table">
            <thead className="table-light">
              <tr>
                <th style={{ width: '45px', textAlign: 'center' }}>#</th>
                <th style={{ width: '135px' }}>{t("Date & Time")}</th>
                <th style={{ width: '115px' }}>{t("Reference #")}</th>
                <th>{t("Particulars & Party Details")}</th>
                <th style={{ width: '160px' }}>{t("Category / Head")}</th>
                <th style={{ width: '110px', textAlign: 'center' }}>{t("Mode")}</th>
                <th style={{ width: '110px', textAlign: 'right' }}>{t("Credit (Income)")}</th>
                <th style={{ width: '110px', textAlign: 'right' }}>{t("Debit (Expense)")}</th>
                <th style={{ width: '120px', textAlign: 'right' }}>{t("Running Balance")}</th>
                <th style={{ width: '90px', textAlign: 'center' }}>{t("Status")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="text-center py-5">
                    <Spinner color="primary" size="sm" className="me-2" />
                    <span className="text-muted font-size-13">{t("Loading passbook records...")}</span>
                  </td>
                </tr>
              ) : displayedItems.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center py-5">
                    <div className="text-muted mb-2">
                      <RiBookOpenLine className="font-size-36 text-muted opacity-50" />
                    </div>
                    <h6 className="font-weight-bold text-dark font-size-14 mb-1">
                      {t("No Transactions Found for this Period")}
                    </h6>
                    <p className="text-muted font-size-12 mb-3">
                      {t("Try changing the date range, financial year, or clearing search filters.")}
                    </p>
                    <Button
                      type="button"
                      color="primary"
                      size="sm"
                      className="rounded-pill px-3"
                      onClick={() => {
                        setFilterPreset('this_fy');
                        setTypeFilter('all');
                        setCategoryFilter('all');
                        setPaymentModeFilter('all');
                        setSearchQuery('');
                      }}
                    >
                      {t("Reset to Current FY")}
                    </Button>
                  </td>
                </tr>
              ) : (
                displayedItems.map((row, index) => {
                  const isCredit = row.type === 'Credit';
                  const isDebit = row.type === 'Debit';

                  return (
                    <tr key={row.id || index} className="passbook-row">
                      {/* Index */}
                      <td className="text-center">
                        <span className="badge bg-light text-muted border font-size-10 font-weight-bold">
                          {index + 1}
                        </span>
                      </td>

                      {/* Date & Time */}
                      <td>
                        <div className="font-weight-semibold text-dark font-size-12">
                          {row.date ? moment(row.date).format('DD MMM YYYY') : '—'}
                        </div>
                        <div className="font-size-10 text-muted d-flex align-items-center gap-1">
                          <RiCalendarEventLine className="font-size-10" />
                          {row.date ? moment(row.date).format('hh:mm A') : ''}
                        </div>
                      </td>

                      {/* Reference # */}
                      <td>
                        <span className="badge bg-light text-primary border font-monospace font-weight-bold font-size-11 px-2 py-1">
                          {row.ref_no || `TXN-${row.id}`}
                        </span>
                        {row.invoice_no && (
                          <div className="font-size-10 text-muted mt-0.5 font-monospace">
                            Inv: {row.invoice_no}
                          </div>
                        )}
                      </td>

                      {/* Particulars & Party Details */}
                      <td>
                        <div className="d-flex align-items-start gap-2">
                          <div
                            className={`rounded-circle d-flex align-items-center justify-content-center font-weight-bold font-size-11 flex-shrink-0 mt-0.5 ${
                              isCredit ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'
                            }`}
                            style={{ width: '28px', height: '28px' }}
                          >
                            {isCredit ? <RiArrowDownLine /> : <RiArrowUpLine />}
                          </div>
                          <div>
                            <div className="font-weight-bold text-dark font-size-13">
                              {row.party_name || 'Transaction'}
                            </div>
                            {row.party_detail && (
                              <div className="font-size-11 text-muted">
                                {row.party_detail}
                              </div>
                            )}
                            {row.description && (
                              <div className="font-size-11 text-secondary mt-0.5">
                                {row.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Category */}
                      <td>
                        {row.source === 'payment_receipt' ? (
                          <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-2 py-1 rounded font-size-11 font-weight-semibold">
                            🦷 {t("Patient Collection")}
                          </span>
                        ) : row.source === 'petty_cash' ? (
                          <span className={`badge ${isCredit ? 'bg-success-subtle text-success border border-success-subtle' : 'bg-warning-subtle text-warning border border-warning-subtle'} px-2 py-1 rounded font-size-11 font-weight-semibold`}>
                            {isCredit ? '📥 Petty Cash Inflow' : '💸 Clinic Expense'}
                          </span>
                        ) : row.source === 'bank_deposit' ? (
                          <span className="badge bg-info-subtle text-info border border-info-subtle px-2 py-1 rounded font-size-11 font-weight-semibold">
                            🏦 {t("Bank Deposit")}
                          </span>
                        ) : (
                          <span className="badge bg-light text-dark border px-2 py-1 rounded font-size-11">
                            {row.category || 'General'}
                          </span>
                        )}
                      </td>

                      {/* Payment Mode */}
                      <td className="text-center">
                        <span className="badge bg-light text-dark border font-size-11 font-weight-medium px-2 py-1">
                          {row.payment_mode || 'Cash'}
                        </span>
                      </td>

                      {/* Credit (+ Income) */}
                      <td className="text-end">
                        {row.credit > 0 ? (
                          <span className="font-weight-bold text-success font-size-13">
                            +₹{parseFloat(row.credit).toLocaleString('en-IN')}
                          </span>
                        ) : (
                          <span className="text-muted font-size-12">—</span>
                        )}
                      </td>

                      {/* Debit (- Expense) */}
                      <td className="text-end">
                        {row.debit > 0 ? (
                          <span className="font-weight-bold text-danger font-size-13">
                            -₹{parseFloat(row.debit).toLocaleString('en-IN')}
                          </span>
                        ) : (
                          <span className="text-muted font-size-12">—</span>
                        )}
                      </td>

                      {/* Cumulative Running Balance */}
                      <td className="text-end">
                        <span className={`font-weight-bold font-size-13 ${(row.balance || 0) >= 0 ? 'text-dark' : 'text-danger'}`}>
                          ₹{parseFloat(row.balance || 0).toLocaleString('en-IN')}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="text-center">
                        <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-0.5 rounded-pill font-size-10 font-weight-semibold d-inline-flex align-items-center gap-0.5">
                          ✓ {t("Completed")}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>

            {/* Table Footer Totals */}
            {displayedItems.length > 0 && (
              <tfoot className="table-light border-top-2">
                <tr className="font-weight-bold font-size-12">
                  <td colSpan="6" className="text-end text-dark py-2.5">
                    {t("Total Inflows & Outflows for Filtered Period:")}
                  </td>
                  <td className="text-end text-success font-size-13 py-2.5">
                    +₹{(passbookData.summary.totalCredit || 0).toLocaleString('en-IN')}
                  </td>
                  <td className="text-end text-danger font-size-13 py-2.5">
                    -₹{(passbookData.summary.totalDebit || 0).toLocaleString('en-IN')}
                  </td>
                  <td className="text-end text-primary font-size-13 py-2.5">
                    ₹{(passbookData.summary.netCashflow || 0).toLocaleString('en-IN')}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </Table>
        </div>
      </div>
    </div>
  );
};

export default ManagePassbook;
